import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import '../utils/session.ts';
import { User, AtSign } from 'lucide-react';

// Utility to parse JWT and extract the role
function parseJwt(token: string): any | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(decodeURIComponent(atob(base64).split('').map(c =>
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join('')));
  } catch {
    return null;
  }
}
let logoUrl = '';
const backgroundImage = 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2574&auto=format&fit=crop';

const CustomerLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Extract from query params
  const params = new URLSearchParams(location.search);
  const restaurantGuid = params.get('restaurantGuid') || '';
  const tableId = params.get('tableId') || '';
  // const [backgroundImage, setBackgroundImage] = useState('https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2574&auto=format&fit=crop');

  const [form, setForm] = useState({
    name: '',
    contactInfo: '', // Merged field
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [platformInfo, setPlatformInfo] = useState<any | null>(null);
  const [platformLoading, setPlatformLoading] = useState(true);

  // Fetch platform info on mount
  React.useEffect(() => {

    async function fetchPlatformInfo() {
      if (!restaurantGuid || !tableId) {
        setError('Invalid restaurant. Please scan the table QR again.');
        setPlatformLoading(false);
        return;
      }
      try {
        const res = await axiosInstance.post(`/Auth/platforminfo/${restaurantGuid}/${tableId}`);
        if (res.data && res.data.restaurantName && res.data.tableLabel) {
          setPlatformInfo(res.data);
          logoUrl = res.data.restaurantLogoUrl?.startsWith('http')
            ? res.data.restaurantLogoUrl
            : `${process.env.REACT_APP_API_BASE}/${res.data.restaurantLogoUrl?.replace(/^\/+/g, '')}`;
        } else {
          setError('Invalid restaurant. Please scan the table QR again.');
        }
      } catch {
        setError('Invalid restaurant. Please scan the table QR again.');
      } finally {
        setPlatformLoading(false);
      }
    }
    fetchPlatformInfo();
  }, [restaurantGuid, tableId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { name, contactInfo } = form;

    // Validate: Name and contact info are required
    if (!name || !contactInfo) {
      setError('Name and either Mobile or Email are required.');
      return;
    }
    if (!restaurantGuid) {
      setError('Invalid restaurant. Please scan the table QR again.');
      return;
    }

    // Determine if contactInfo is email or mobile
    // --- START OF ADDED VALIDATION ---
    // Simple validation for name
    if (!name.trim()) {
      setError('Please enter your full name.');
      return;
    }

    let email;
    let mobile;

    // Regular expressions for validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10}$/; // A simple regex for 10 digits

    if (contactInfo.includes('@')) {
      // It looks like an email, validate as such
      if (!emailRegex.test(contactInfo)) {
        setError('Please enter a valid email address or mobile number.');
        return;
      }
      email = contactInfo;
    } else {
      // It looks like a mobile number, validate as such
      if (!mobileRegex.test(contactInfo)) {
        setError('Please enter a valid 10 mobile number (without country code) or email address.');
        return;
      }
      mobile = contactInfo;
    }
    // --- END OF ADDED VALIDATION ---

    setLoading(true);
    try {
      const res = await axiosInstance.post('/auth/customer-token', {
        name,
        email: email || undefined,
        mobile: mobile || undefined,
        tableId,
        restaurantGuid,
      });

      // JWT role check
      const payload = parseJwt(res.data.token);
      const jwtRole =
        (payload && (payload["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] || payload.role || '')).toLowerCase();
      if (jwtRole !== 'customer') {
        setError('These details do not match a customer account. Please try again.');
        setLoading(false);
        return;
      }

      // Use platformInfo from state (already validated)
      if (!platformInfo || !platformInfo.restaurantName || !platformInfo.tableLabel) {
        setError('Invalid restaurant info. Please scan the table QR again.');
        setLoading(false);
        return;
      }
      // Compose session object
      const userData = {
        ...res.data,
        platformInfo: {
          restaurantName: platformInfo.restaurantName,
          restaurantLogoUrl: logoUrl,
          tableLabel: platformInfo.tableLabel,
          isQRMenuOrderEnabled: platformInfo.isQRMenuOrderEnabled || false,
        }
      };
      localStorage.setItem('Dinelyt_session', JSON.stringify(userData));
      console.log('Customer session saved:', userData);
      // Redirect to customer panel (including restaurantGuid and tableId in URL)
      navigate(`/customer/${restaurantGuid}/${tableId}`);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Login failed. Please check your details and try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen p-4 bg-cover bg-center transition-all duration-1000"
      style={{ backgroundImage: `url('${backgroundImage}')` }}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>

      {/* Show loading spinner while fetching platform info */}
      {platformLoading ? (
        <div className="relative w-full max-w-md bg-white/30 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden flex items-center justify-center min-h-[300px]">
          <span className="text-white text-lg">Loading...</span>
        </div>
      ) : error && !platformInfo ? ( // Only show full-screen error if platform info fails
        <div className="relative w-full max-w-md bg-white/30 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden flex items-center justify-center min-h-[300px]">
          <span className="text-red-500 text-lg font-bold text-center p-8">{error}</span>
        </div>
      ) : (
        <div className="relative w-full max-w-md bg-white/30 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8 md:p-10">
            <div className="flex justify-center mb-6">
              <img src={logoUrl || ''} alt="Restaurant Logo" className="h-16 object-contain" onError={(e) => e.currentTarget.style.display = 'none'} />
            </div>
            <h2 className="text-3xl font-bold text-white text-center mb-2">{platformInfo?.restaurantName || 'Welcome'}</h2>
            <p className="text-white/80 text-center mb-8">Please sign in to continue</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/70 w-5 h-5" />
                <input
                  name="name"
                  placeholder="Full Name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full pl-12 pr-3 py-3 bg-white/20 text-white placeholder-white/70 border-2 border-transparent focus:border-white/50 rounded-lg outline-none transition duration-300"
                  required
                />
              </div>

              <div className="relative">
                <AtSign className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/70 w-5 h-5" />
                <input
                  name="contactInfo"
                  type="text"
                  placeholder="Email or Mobile Number"
                  value={form.contactInfo}
                  onChange={handleChange}
                  className="w-full pl-12 pr-3 py-3 bg-white/20 text-white placeholder-white/70 border-2 border-transparent focus:border-white/50 rounded-lg outline-none transition duration-300"
                  required
                />
              </div>

              {platformInfo?.tableLabel && (
                <div className="pt-2 text-center text-white/80 text-sm">
                  Table: <span className="font-mono font-bold tracking-wider">{platformInfo.tableLabel}</span>
                </div>
              )}
              {error && <div className="bg-red-500/50 text-white p-3 rounded-lg text-center text-sm">{error}</div>}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-3 font-bold rounded-lg hover:bg-blue-700 disabled:bg-blue-400/50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-300 shadow-lg"
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerLogin;
